import { useState } from "react";

// Drives a whole "section" of editable fields (e.g. Basic Details) behind a
// single Edit/Save/Cancel toggle, instead of one edit control per field.
//
// `fields` is a config array of { key, label, value, validate }. Adding a
// new editable field to a section is just adding an entry to that array -
// this hook and the UI around it don't need to change.
//
// `onSave(changedFields)` is called with an object containing ONLY the
// fields whose value actually differs from the original `value` in the
// config (a dirty-field diff), and only if at least one field changed.
export default function useEditableSection(fields, onSave) {
    const [isEditing, setIsEditing] = useState(false);
    const [values, setValues] = useState({});
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const startEdit = () => {
        const initialValues = {};
        fields.forEach((field) => {
            initialValues[field.key] = field.value ?? "";
        });
        setValues(initialValues);
        setErrors({});
        setIsEditing(true);
    };

    const cancelEdit = () => {
        setValues({});
        setErrors({});
        setIsEditing(false);
    };

    const setFieldValue = (key, value) => {
        setValues((prev) => ({ ...prev, [key]: value }));
        if (errors[key]) {
            setErrors((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        }
    };

    const getDirtyFields = () => {
        const changed = {};
        fields.forEach((field) => {
            const current = (values[field.key] ?? "").toString().trim();
            const original = (field.value ?? "").toString().trim();
            if (current !== original) {
                changed[field.key] = current;
            }
        });
        return changed;
    };

    const save = async () => {
        // Validate every visible field in the section, not just the changed
        // ones, since they're all currently presented as editable inputs.
        const newErrors = {};
        fields.forEach((field) => {
            if (field.validate) {
                const error = field.validate(values[field.key]);
                if (error) newErrors[field.key] = error;
            }
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const changed = getDirtyFields();
        if (Object.keys(changed).length === 0) {
            setIsEditing(false);
            return;
        }

        setSaving(true);
        try {
            await onSave(changed);
            setIsEditing(false);
        } catch (err) {
            setErrors({ _section: err?.message || "Failed to save. Please try again." });
        } finally {
            setSaving(false);
        }
    };

    return {
        isEditing,
        values,
        errors,
        saving,
        startEdit,
        cancelEdit,
        save,
        setFieldValue,
    };
}
